import {
  Lightbulb,
  Target,
  Users,
  type LucideIcon,
} from "lucide-react";

/**
 * Single source of truth for the /resurser/guider overview.
 *
 * Every guide that has a route under `routes.guides.*` MUST have an entry here,
 * so the overview can never silently omit a published guide. A contract test
 * enforces this 1:1 mapping.
 *
 * Translatable fields carry both the i18n key and a Swedish fallback; the
 * overview resolves them with `t(key, fallback)`.
 */
export interface GuideI18n {
  key: string;
  fallback: string;
}

export interface GuideMeta {
  /** routes.ts key under `guides.*`, used to derive the localized URL. */
  routeKey: string;
  icon: LucideIcon;
  readingTime: string;
  /** ISO date (YYYY-MM-DD); the overview sorts by this, newest first. */
  publishDate: string;
  title: GuideI18n;
  description: GuideI18n;
  difficulty: GuideI18n;
  topics: GuideI18n[];
}

const difficulty = {
  beginner: {
    key: "resources:guides.difficulty.beginner",
    fallback: "Nybörjare",
  },
  intermediate: {
    key: "resources:guides.difficulty.intermediate",
    fallback: "Medel",
  },
  whitepaper: {
    key: "resources:guides.difficulty.whitepaper",
    fallback: "Whitepaper",
  },
} as const;

const topic = {
  scheduling: {
    key: "resources:guides.topics.scheduling",
    fallback: "Schemaläggning",
  },
  optimization: {
    key: "resources:guides.topics.optimization",
    fallback: "Optimering",
  },
  efficiency: {
    key: "resources:guides.topics.efficiency",
    fallback: "Effektivitet",
  },
  staffPlanning: {
    key: "resources:guides.topics.staff_planning",
    fallback: "Personalplanering",
  },
  resourceAllocation: {
    key: "resources:guides.topics.resource_allocation",
    fallback: "Resursallokering",
  },
  workforce: {
    key: "resources:guides.topics.workforce",
    fallback: "Arbetsstyrka",
  },
  ai: { key: "resources:guides.topics.ai", fallback: "AI" },
  implementation: {
    key: "resources:guides.topics.implementation",
    fallback: "Implementering",
  },
  changeManagement: {
    key: "resources:guides.topics.change_management",
    fallback: "Förändringsledning",
  },
} as const;

export const GUIDES: GuideMeta[] = [
  {
    routeKey: "guides.optimizeSchedule",
    icon: Target,
    readingTime: "8 min",
    publishDate: "2025-05-25",
    title: {
      key: "resources:guides.optimize.title",
      fallback: "Optimera hemtjänstschema",
    },
    description: {
      key: "resources:guides.optimize.description",
      fallback:
        "Lär dig hur du kan optimera dina hemtjänstscheman för maximal effektivitet och personalnöjdhet.",
    },
    difficulty: difficulty.beginner,
    topics: [topic.scheduling, topic.optimization, topic.efficiency],
  },
  {
    routeKey: "guides.comprehensiveAiImplementation",
    icon: Lightbulb,
    readingTime: "12 min",
    publishDate: "2025-05-25",
    title: {
      key: "resources:guides.prepare.title",
      fallback: "Omfattande AI-implementeringsguide",
    },
    description: {
      key: "resources:guides.prepare.description",
      fallback:
        "En steg-för-steg guide för att framgångsrikt implementera AI-lösningar i din hemtjänstorganisation.",
    },
    difficulty: difficulty.intermediate,
    topics: [topic.ai, topic.implementation, topic.changeManagement],
  },
  {
    routeKey: "guides.effectiveStaffPlanning",
    icon: Users,
    readingTime: "10 min",
    publishDate: "2025-05-15",
    title: {
      key: "resources:guides.staff.title",
      fallback: "Effektiv personalplanering",
    },
    description: {
      key: "resources:guides.staff.description",
      fallback:
        "Strategier och metoder för att optimera personalplanering i hemtjänsten.",
    },
    difficulty: difficulty.intermediate,
    topics: [topic.staffPlanning, topic.resourceAllocation, topic.workforce],
  },
];
